import { useState, useCallback, useRef, useEffect, useMemo, memo, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowUp, Mic, Square, RefreshCw, PanelLeft, CircleDot, ListChecks, ChevronRight, Calendar, Sun, ImagePlus, X, FileText, Presentation, Mail, type LucideIcon } from 'lucide-react'
import type { ScreenName, DeepLink } from '../../shared/types/navigation'
import { TypingIndicator } from '@tendbot/ui'
import { StreamingBubble, ToolActivityIndicator, BatchApprovalSheet, MessageContextMenu, BriefingSkeleton } from './components'
import { useRealtimeEvents } from './useRealtimeEvents'
import { useAuth } from '../auth'
import { SectionDivider } from './components'
import { getDateLabel, showDateSeparator, hasExternalContent } from './utils'
import { chipStagger } from '@tendbot/ui'
import { ChatProvider } from './ChatContext'
import { MessageRenderer, WelcomeLoader } from './components'
import { useChatLoader } from './useChatLoader'
import { useConversationList } from './useConversationList'
import { useMessageUpdate } from './useMessageUpdate'
import { useAutoScroll } from './useAutoScroll'
import { useDeclineHandler } from './useDeclineHandler'
import { AppSidebar } from './AppSidebar'
import { ConversationDrawer } from './ConversationDrawer'
import { useStreamingChat } from './useStreamingChat'
import { useChatVoiceInput } from './useChatVoiceInput'
import { uploadChatImage } from './api'
import { useChatCardActions } from './useChatCardActions'
import { useConversationManager } from './useConversationManager'
import { useSetDebugData } from '../debug'
import { FlagModal, type FlagTarget } from '../flag'
import { EmailDetailSheet } from '../mail'
import { useTrialStatus } from './useTrialStatus'
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight'
import TrialExpiredOverlay from './components/TrialExpiredOverlay'
import { formatDisplayTime } from '@tendbot/ui'

const suggestionIcons: Record<string, LucideIcon> = {
  Calendar,
  Sun,
  FileText,
  Presentation,
  Mail,
}

interface PendingImage {
  localId: string        // temp ID for display before upload completes
  localUrl: string       // blob URL for instant preview
  serverId?: string      // set after upload succeeds
  serverUrl?: string     // signed URL from server
  uploading: boolean
  error?: boolean
  errorMsg?: string
}

const VOICE_BAR_MULTIPLIERS = [0.6, 1.0, 0.8, 1.0, 0.7, 0.9, 1.0, 0.65, 0.85]

export interface ChatInputHandle {
  prefillReply: (quotedContent: string) => void
}

const ChatInput = memo(function ChatInput({ onSend, disabled, sendDisabled, ref: handleRef }: { onSend: (content: string, hint?: string, imageIds?: string[], imageUrls?: string[]) => void; disabled: boolean; sendDisabled?: boolean; ref?: React.Ref<ChatInputHandle> }) {
  const { t } = useTranslation('chat')
  const [value, setValue] = useState('')
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(handleRef, () => ({
    prefillReply: (quotedContent: string) => {
      const quoted = quotedContent.split('\n').map(l => `> ${l}`).join('\n')
      setValue(prev => prev ? `${quoted}\n\n${prev}` : `${quoted}\n\n`)
      requestAnimationFrame(() => {
        const el = textareaRef.current
        if (!el) return
        el.focus()
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`
        const len = el.value.length
        el.setSelectionRange(len, len)
      })
    },
  }), [])
  const { isSupported, state: voiceState, elapsedSeconds, audioLevel, error: voiceError, start: voiceStart, stop: voiceStop } = useChatVoiceInput()

  const isRecording = voiceState === 'recording'
  const isProcessing = voiceState === 'processing'

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])

  const addImage = useCallback((file: File | Blob) => {
    if (pendingImages.length >= 5) return
    // Show local preview immediately
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const localUrl = URL.createObjectURL(file)
    setPendingImages(prev => [...prev, { localId, localUrl, uploading: true }])

    // Upload in background
    uploadChatImage(file)
      .then(result => {
        setPendingImages(prev => prev.map(img =>
          img.localId === localId
            ? { ...img, serverId: result.id, serverUrl: result.thumbnailUrl || result.url, uploading: false }
            : img
        ))
      })
      .catch(err => {
        console.error('Image upload failed:', err?.message || err)
        setPendingImages(prev => prev.map(img =>
          img.localId === localId ? { ...img, uploading: false, error: true, errorMsg: err?.message } : img
        ))
      })
  }, [pendingImages.length])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    addImage(file)
  }, [addImage])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) addImage(file)
        return
      }
    }
  }, [addImage])

  const removeImage = useCallback((localId: string) => {
    setPendingImages(prev => {
      const img = prev.find(i => i.localId === localId)
      if (img) URL.revokeObjectURL(img.localUrl)
      return prev.filter(i => i.localId !== localId)
    })
  }, [])

  // Only allow sending when all images are uploaded (no errors, no pending uploads)
  const allUploaded = pendingImages.length === 0 || pendingImages.every(img => img.serverId && !img.error)
  const hasUploadErrors = pendingImages.some(img => img.error)

  // Handle voice stop → auto-send
  const handleVoiceStop = useCallback(async () => {
    const message = await voiceStop()
    if (message) onSend(message)
  }, [voiceStop, onSend])

  const send = useCallback(() => {
    const hasText = value.trim().length > 0
    const readyImages = pendingImages.filter(img => img.serverId && !img.error)
    const hasImages = readyImages.length > 0
    if (!hasText && !hasImages) return
    const imageIds = hasImages ? readyImages.map(img => img.serverId!) : undefined
    const imageUrls = hasImages ? readyImages.map(img => img.serverUrl!) : undefined
    onSend(value, undefined, imageIds, imageUrls)
    setValue('')
    for (const img of pendingImages) URL.revokeObjectURL(img.localUrl)
    setPendingImages([])
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }, [value, onSend, pendingImages])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!sendDisabled) send()
    }
  }, [send, sendDisabled])

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-3 sm:py-4 lg:py-5 border-t border-gold/10 bg-gradient-to-t from-midnight via-midnight to-transparent">
      <div className={`max-w-3xl mx-auto glass-card rounded-2xl sm:rounded-[20px] transition-shadow duration-300 ${isRecording ? 'animate-voice-glow' : 'focus-within:ring-1 focus-within:ring-gold/30 focus-within:shadow-[0_0_24px_rgba(200,149,48,0.1)]'}`}>
        {/* Image preview strip */}
        {pendingImages.length > 0 && (
          <div className="flex gap-2 px-4 sm:px-5 pt-3 overflow-x-auto">
            {pendingImages.map(img => (
              <div key={img.localId} className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border ${img.error ? 'border-rose/40' : 'border-gold/20'}`}>
                <img
                  src={img.localUrl}
                  alt=""
                  className={`w-full h-full object-cover ${img.uploading ? 'opacity-50' : ''}`}
                />
                {img.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-gold/40 border-t-gold rounded-full animate-spin" />
                  </div>
                )}
                {img.error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-midnight/60">
                    <span className="text-rose text-xs font-medium">!</span>
                  </div>
                )}
                <button
                  onClick={() => removeImage(img.localId)}
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-midnight/90 border border-gold/30 flex items-center justify-center text-pearl/70 hover:text-ivory transition-colors"
                  aria-label={t('input.removePhoto')}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        {hasUploadErrors && (
          <div className="px-4 sm:px-5 pt-1">
            <span className="text-xs text-rose/70">{t('input.uploadFailed')}</span>
            {pendingImages.filter(i => i.errorMsg).map(i => (
              <span key={i.localId} className="block text-xs text-rose/50">{i.errorMsg}</span>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="flex items-end gap-3 px-4 sm:px-5 py-3 sm:py-3.5">
          {isRecording ? (
            <div className="flex-1 flex items-center gap-1 min-h-6 sm:min-h-7 py-0.5 sm:py-1">
              {VOICE_BAR_MULTIPLIERS.map((mult, i) => (
                <span
                  key={i}
                  className="w-[3px] rounded-full bg-gold/80 transition-[height] duration-100"
                  style={{ height: `${Math.max(4, audioLevel * mult * 28)}px` }}
                />
              ))}
              <span className="ml-3 text-sm text-ivory/80 tabular-nums">
                {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
              </span>
            </div>
          ) : isProcessing ? (
            <div className="flex-1 flex items-center gap-2 min-h-6 sm:min-h-7 py-0.5 sm:py-1">
              <div className="w-4 h-4 border-2 border-gold/40 border-t-gold rounded-full animate-spin" />
              <span className="text-sm text-ivory/60">{t('input.transcribing')}</span>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={t('input.placeholder')}
              disabled={disabled}
              className="flex-1 bg-transparent text-ivory placeholder:text-pearl/55 text-sm sm:text-[15px] py-[7px] sm:py-1.5 leading-5 sm:leading-6 focus:outline-none disabled:opacity-50 resize-none overflow-y-auto max-h-40"
            />
          )}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Photo button — hidden during recording/processing */}
            {!isRecording && !isProcessing && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="absolute w-px h-px overflow-hidden opacity-0"
                  style={{ clip: 'rect(0,0,0,0)' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || pendingImages.some(i => i.uploading) || pendingImages.length >= 5}
                  aria-label={t('input.addPhoto')}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-pearl/50 hover:text-pearl/70 hover:bg-pearl/5"
                >
                  <ImagePlus size={18} />
                </button>
              </>
            )}
            {isSupported && !isProcessing && (
              <button
                onClick={() => isRecording ? handleVoiceStop() : voiceStart()}
                disabled={disabled}
                aria-label={isRecording ? t('input.voiceStop') : t('input.voiceStart')}
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                  isRecording
                    ? 'bg-gold/15 text-gold'
                    : 'text-pearl/50 hover:text-pearl/70 hover:bg-pearl/5'
                }`}
              >
                {isRecording ? <Square size={14} fill="currentColor" /> : <Mic size={18} />}
              </button>
            )}
            {!isRecording && !isProcessing && (
              <button
                onClick={send}
                disabled={disabled || sendDisabled || !allUploaded || (!value.trim() && pendingImages.length === 0)}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gold flex items-center justify-center hover:bg-gold-light transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
              >
                <ArrowUp size={16} strokeWidth={2.5} className="text-midnight" />
              </button>
            )}
          </div>
        </div>
        {voiceError && (
          <div className="px-4 sm:px-5 pb-2">
            <span className="text-xs text-rose/70">{voiceError}</span>
          </div>
        )}
      </div>
    </div>
  )
})

interface Props {
  onNavigate?: (screen: ScreenName, deepLink?: DeepLink) => void
  activeTab?: ScreenName
  sidebarOnly?: boolean
  deepLink?: DeepLink | null
  onDeepLinkConsumed?: () => void
}

export default function Chat({ onNavigate, activeTab = 'chat', sidebarOnly = false, deepLink, onDeepLinkConsumed }: Props) {
  const { t } = useTranslation('chat')
  const { user } = useAuth()
  const { messages, setMessages, suggestions, conversationId, setConversationId, loading, refresh } = useChatLoader()
  const { conversations, activeConversationId, loading: convListLoading, refetch: refetchConversations, updateConversationPreview, updateConversationTitle } = useConversationList()
  const [showExpiredOverlay, setShowExpiredOverlay] = useState(false)
  const [tierBlockReason, setTierBlockReason] = useState<'TRIAL_EXPIRED' | 'SUBSCRIPTION_REQUIRED'>('TRIAL_EXPIRED')
  const updateMessage = useMessageUpdate(setMessages)
  const tryDecline = useDeclineHandler(setMessages)
  const { tier, trialDaysLeft, subscriptionStatus } = useTrialStatus()
  const [batchSheetOpen, setBatchSheetOpen] = useState(false)

  // Determine if the user is blocked from sending (expired trial or inactive account)
  const isTierBlocked = (tier === 'trial' && trialDaysLeft === 0)
    || (tier === 'managed' && (subscriptionStatus === 'canceled' || subscriptionStatus === 'unpaid'))

  // Count pending approvals for batch mode trigger
  const pendingApprovalCount = useMemo(
    () => messages.filter(m => m.approval?.status === 'pending').length,
    [messages],
  )

  // Handle batch approve results — update message states and add summary reply
  const handleBatchApproved = useCallback((
    approvedMessageIds: string[],
    reply?: { messageId: string; content: string; timestamp: string },
  ) => {
    setMessages(prev => {
      const updated = prev.map(m =>
        approvedMessageIds.includes(m.id) && m.approval
          ? { ...m, approval: { ...m.approval, status: 'approved' as const } }
          : m,
      )
      if (reply) {
        updated.push({
          id: reply.messageId,
          type: 'assistant',
          content: reply.content,
          timestamp: reply.timestamp,
          displayTime: formatDisplayTime(reply.timestamp),
        })
      }
      return updated
    })
  }, [setMessages])

  // Streaming
  const onTierBlocked = useCallback((code: string) => {
    setTierBlockReason(code === 'SUBSCRIPTION_REQUIRED' ? 'SUBSCRIPTION_REQUIRED' : 'TRIAL_EXPIRED')
    setShowExpiredOverlay(true)
  }, [])

  const { isTyping, streamingMessage, streamCompletedIds, handleSendMessage, handleNewMessage } = useStreamingChat({
    conversationId, setConversationId, setMessages, tryDecline, updateConversationPreview, updateConversationTitle, t,
    onTierBlocked,
  })

  // Subscribe to async push events only (message.new for briefings, etc.)
  useRealtimeEvents(user?.id, handleNewMessage)

  // Refetch messages when app resumes from background (e.g. notification tap)
  useEffect(() => {
    const handler = async () => {
      if (document.visibilityState !== 'visible') return
      const fresh = await refresh()
      for (const m of fresh) streamCompletedIds.current.add(m.id)
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [refresh, streamCompletedIds])

  // Conversation sidebar / drawer
  const { drawerOpen, setDrawerOpen, sidebarCollapsed, toggleSidebarCollapsed, handleNewChat, switchToConversation } = useConversationManager({
    conversationId, setConversationId, setMessages, refetchConversations, streamCompletedIds,
  })

  // Handle deep link: create new conversation and auto-send a message
  const pendingMessageRef = useRef<string | null>(null)

  useEffect(() => {
    if (!deepLink || deepLink.type !== 'send-message' || !deepLink.message) return
    onDeepLinkConsumed?.()
    pendingMessageRef.current = deepLink.message
    handleNewChat()
  }, [deepLink]) // eslint-disable-line react-hooks/exhaustive-deps

  // Send the pending message once the new conversation is ready
  useEffect(() => {
    if (!pendingMessageRef.current || !conversationId) return
    const message = pendingMessageRef.current
    pendingMessageRef.current = null
    handleSendMessage(message)
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Long-press context menu + flag modal target
  const [flagTarget, setFlagTarget] = useState<FlagTarget | null>(null)
  const [contextMenuMessageId, setContextMenuMessageId] = useState<string | null>(null)
  const [briefingEmailId, setBriefingEmailId] = useState<string | null>(null)
  const chatInputRef = useRef<ChatInputHandle | null>(null)

  const handleLongPress = useCallback((messageId: string) => {
    setContextMenuMessageId(messageId)
  }, [])

  const handleOpenEmail = useCallback((emailId: string) => {
    setBriefingEmailId(emailId)
  }, [])

  // Card actions
  const { chatActions, handleRetry, handleSuggestionClick } = useChatCardActions({
    messages, setMessages, updateMessage, handleSendMessage, onNavigate,
    onLongPress: handleLongPress,
    onOpenEmail: handleOpenEmail,
  })

  const contextMenuMessage = useMemo(
    () => messages.find(m => m.id === contextMenuMessageId) ?? null,
    [messages, contextMenuMessageId],
  )

  const handleMenuCopy = useCallback(() => {
    const content = contextMenuMessage?.content ?? ''
    if (!content) return
    navigator.clipboard?.writeText(content).catch(err => console.error('Copy failed:', err))
  }, [contextMenuMessage])

  const handleMenuReply = useCallback(() => {
    const content = contextMenuMessage?.content ?? ''
    if (!content) return
    chatInputRef.current?.prefillReply(content)
  }, [contextMenuMessage])

  const handleMenuSaveToNotebook = useCallback(() => {
    if (!contextMenuMessage) return
    chatActions.onSaveToNotebook(contextMenuMessage.id)
  }, [contextMenuMessage, chatActions])

  const handleMenuFlag = useCallback(() => {
    if (!contextMenuMessage) return
    setFlagTarget({
      message: contextMenuMessage,
      conversationId,
      recentMessages: messages.slice(-5),
    })
  }, [contextMenuMessage, conversationId, messages])

  // Handle single approve from within batch sheet
  const handleBatchSingleApprove = useCallback((messageId: string) => {
    chatActions.onApprove(messageId)
  }, [chatActions])

  // Sync debug data
  const setDebugData = useSetDebugData()
  useEffect(() => {
    setDebugData(conversationId, messages)
  }, [conversationId, messages, setDebugData])

  // Auto-scroll (also triggers when keyboard opens/closes to keep content visible)
  const { keyboardOpen } = useKeyboardHeight()
  const messagesEndRef = useAutoScroll([messages, isTyping, streamingMessage, keyboardOpen])

  const sidebarElement = (
    <AppSidebar
      conversations={conversations}
      activeConversationId={activeConversationId}
      currentConversationId={conversationId}
      collapsed={sidebarCollapsed}
      onToggleCollapse={toggleSidebarCollapsed}
      onSelectConversation={(id) => { switchToConversation(id); if (sidebarOnly) onNavigate?.('chat') }}
      onNewChat={() => { handleNewChat(); if (sidebarOnly) onNavigate?.('chat') }}
      onNavigate={onNavigate}
      activeTab={activeTab}
      loading={convListLoading}
    />
  )

  if (sidebarOnly) {
    return sidebarElement
  }

  if (loading) {
    return (
      <div className="flex h-full flex-1">
        <div className="hidden lg:flex">
          <AppSidebar
            conversations={conversations}
            activeConversationId={activeConversationId}
            currentConversationId={conversationId}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
            onSelectConversation={switchToConversation}
            onNewChat={handleNewChat}
            onNavigate={onNavigate}
            activeTab={activeTab}
            loading={convListLoading}
          />
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          <WelcomeLoader />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-1">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <AppSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          currentConversationId={conversationId}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
          onSelectConversation={switchToConversation}
          onNewChat={handleNewChat}
          onNavigate={onNavigate}
          activeTab={activeTab}
          loading={convListLoading}
        />
      </div>

      {/* Mobile drawer */}
      <ConversationDrawer
        open={drawerOpen}
        conversations={conversations}
        currentConversationId={conversationId}
        onClose={() => setDrawerOpen(false)}
        onSelectConversation={switchToConversation}
        onNewChat={handleNewChat}
        loading={convListLoading}
      />

      {/* Main chat column */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Subscription success toast */}
        {/* Inactive-account overlay (shown when trial expired or account deactivated) */}
        {showExpiredOverlay && (
          <TrialExpiredOverlay reason={tierBlockReason} onDismiss={() => setShowExpiredOverlay(false)} />
        )}

        {/* Batch approval banner — shown when 3+ approvals pending */}
        {pendingApprovalCount >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-5 sm:px-8 lg:px-12 py-2 border-b border-gold/10"
          >
            <div className="max-w-3xl mx-auto">
              <button
                onClick={() => setBatchSheetOpen(true)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors group"
              >
                <div className="flex items-center gap-2.5">
                  <ListChecks size={16} className="text-gold" />
                  <span className="text-sm font-medium text-gold">
                    {t('approval.batch.reviewAll', { count: pendingApprovalCount })}
                  </span>
                </div>
                <ChevronRight size={14} className="text-gold/60 group-hover:text-gold transition-colors" />
              </button>
            </div>
          </motion.div>
        )}


        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 lg:px-12 py-4 relative">
          <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-midnight to-transparent z-10" />
          <ChatProvider value={chatActions}>
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.map((message, index) => (
              <div key={message.id}>
              {showDateSeparator(messages, index) && (
                <SectionDivider label={getDateLabel(message.timestamp)} />
              )}
              <motion.div
                initial={streamCompletedIds.current.has(message.id) ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={streamCompletedIds.current.has(message.id) ? { delay: 0 } : { delay: index * 0.02 }}
                className={`flex flex-col ${message.type === 'user' && !hasExternalContent(message.content) && !message.sourceEmailId && !message.inboxEmail && !message.triageEmail && !message.quarantineEmail && !message.sentEmail ? 'items-end' : 'items-start'}`}
              >
                <MessageRenderer
                  message={message}
                />
                {message.isRetryable && message.retryContent && (
                  <button
                    onClick={() => handleRetry(message.id, message.retryContent!)}
                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs hover:bg-gold/20 transition-colors"
                  >
                    <RefreshCw size={12} />
                    {t('tryAgain')}
                  </button>
                )}
              </motion.div>
              </div>
            ))}

            {/* Typing / Streaming Indicator — hidden once completed message exists */}
            {(isTyping || streamingMessage) &&
              !(streamingMessage && messages.some(m => m.id === streamingMessage.messageId)) && (
                streamingMessage?.toolName === 'generate_briefing' && !streamingMessage.content && !messages[messages.length - 1]?.briefingCard ? (
                  <div className="flex justify-start">
                    <BriefingSkeleton />
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <div className="glass-card rounded-[16px_16px_16px_4px]">
                      {streamingMessage?.content ? (
                        <StreamingBubble content={streamingMessage.content} toolName={streamingMessage.toolName} />
                      ) : streamingMessage?.toolName ? (
                        <ToolActivityIndicator toolName={streamingMessage.toolName} />
                      ) : (
                        <div className="px-4 py-2"><TypingIndicator /></div>
                      )}
                    </div>
                  </div>
                )
            )}

            <div ref={messagesEndRef} />
          </div>
          </ChatProvider>
        </div>

        {/* Suggestion pills + mobile conversation toggle */}
        <div className="px-5 sm:px-8 lg:px-12 py-2 overflow-x-auto border-t border-gold/5">
          <motion.div
            className="max-w-3xl mx-auto flex flex-wrap items-center gap-1.5 sm:gap-2"
            variants={chipStagger().container}
            initial="hidden"
            animate="visible"
          >
            {/* Mobile conversation drawer toggle */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden flex items-center justify-center w-7 h-7 rounded-full bg-charcoal border border-gold/20 text-pearl/60 hover:text-ivory hover:border-gold/40 transition-colors shrink-0"
              aria-label={t('sidebar.openConversations')}
            >
              <PanelLeft size={14} />
            </button>
            {suggestions.map((suggestion) => {
              const Icon = suggestionIcons[suggestion.icon ?? ''] ?? CircleDot
              return (
                <motion.button
                  key={suggestion.id}
                  variants={chipStagger().item}
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={isTyping}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full text-pearl/50 text-xs hover:text-pearl/70 hover:bg-white/[0.04] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon size={12} className="text-gold/50" />
                  {suggestion.category ? t(`suggestions.${suggestion.category}`, suggestion.text) : suggestion.text}
                </motion.button>
              )
            })}
          </motion.div>
        </div>

        {/* Input Area */}
        <ChatInput ref={chatInputRef} onSend={handleSendMessage} disabled={isTierBlocked} sendDisabled={isTyping} />

        {/* Batch Approval Sheet */}
        <BatchApprovalSheet
          open={batchSheetOpen}
          messages={messages}
          onClose={() => setBatchSheetOpen(false)}
          onApproved={handleBatchApproved}
          onSingleApprove={handleBatchSingleApprove}
        />

        {/* Long-press context menu */}
        <MessageContextMenu
          open={contextMenuMessageId !== null}
          onClose={() => setContextMenuMessageId(null)}
          onCopy={handleMenuCopy}
          onReply={handleMenuReply}
          onSaveToNotebook={handleMenuSaveToNotebook}
          onFlag={handleMenuFlag}
        />

        {/* Flag Conversation Modal */}
        <FlagModal
          isOpen={flagTarget !== null}
          onClose={() => setFlagTarget(null)}
          target={flagTarget}
        />

        {/* Email detail sheet — opened from briefing inbox rows */}
        <AnimatePresence>
          {briefingEmailId && (
            <EmailDetailSheet
              key={briefingEmailId}
              emailId={briefingEmailId}
              onClose={() => setBriefingEmailId(null)}
              onNavigate={(screen, params) => {
                setBriefingEmailId(null)
                onNavigate?.(screen as ScreenName, params ?? undefined)
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
